/** Top filter strip for My Team — shared FilterStripLayout with Utilization. */
import React, { useMemo } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { FilterRequest, ProjectData } from '../../../types/api';
import { getDefaultMyTeamFilterRequest } from '../../../utils/DateUtils';
import MonthRangePicker from '../../MonthRangePicker';
import SelectAllMultiSelect, { FILTER_STANDARD } from '../../common/SelectAllMultiSelect';
import FilterStripLayout from '../FilterStripLayout';

interface MyTeamTopFiltersProps {
  filters: FilterRequest;
  onFiltersChange: (filters: FilterRequest) => void;
  onRefresh: () => void;
  hasUnsavedChanges: boolean;
  availableProjects?: ProjectData[];
  availableEmployees?: string[];
  availableManagers?: Array<{ email: string; name: string }>;
  availableSbus?: string[];
  showEmployeeFilter?: boolean;
  showManagerFilter?: boolean;
  showSbuFilter?: boolean;
  showEmployeeStatusFilter?: boolean;
}

const MyTeamTopFilters: React.FC<MyTeamTopFiltersProps> = ({
  filters,
  onFiltersChange,
  onRefresh,
  hasUnsavedChanges,
  availableProjects = [],
  availableEmployees = [],
  availableManagers = [],
  availableSbus = [],
  showEmployeeFilter = false,
  showManagerFilter = true,
  showSbuFilter = false,
  showEmployeeStatusFilter = true,
}) => {
  const update = (patch: Partial<FilterRequest>) => onFiltersChange({ ...filters, ...patch });

  const handleResetFilters = () => {
    onFiltersChange(getDefaultMyTeamFilterRequest());
  };

  const projectOptions = useMemo(
    () =>
      availableProjects.map((p) => ({
        value: p.project_code,
        label: p.project_name?.trim() ? `${p.project_code} - ${p.project_name}` : p.project_code,
      })),
    [availableProjects]
  );
  const managerOptions = useMemo(() => availableManagers.map((m) => ({ value: m.email, label: m.name })), [availableManagers]);
  const employeeOptions = useMemo(() => availableEmployees.map((e) => ({ value: e, label: e })), [availableEmployees]);
  const sbuOptions = useMemo(() => availableSbus.map((s) => ({ value: s, label: s })), [availableSbus]);

  const colBase = {
    flex: '1 1 160px',
    minWidth: 112,
    maxWidth: 220,
    display: 'flex',
    alignItems: 'flex-end',
  } as const;
  const colDurationSx = { ...colBase, flex: '1.15 1 180px', maxWidth: 240 } as const;

  return (
    <FilterStripLayout onClear={handleResetFilters} onRefresh={onRefresh} hasUnsavedChanges={hasUnsavedChanges} dense>
      <Box sx={colDurationSx}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <MonthRangePicker
            startMonth={filters.start_year && filters.start_month ? { year: filters.start_year, month: filters.start_month } : null}
            endMonth={filters.end_year && filters.end_month ? { year: filters.end_year, month: filters.end_month } : null}
            onChange={(s, e) =>
              update({ start_year: s?.year, start_month: s?.month, end_year: e?.year, end_month: e?.month })
            }
            label="Duration"
          />
        </Box>
      </Box>
      {showEmployeeStatusFilter && (
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: FILTER_STANDARD.fontSize }}>Employment Status</InputLabel>
              <Select
                value={filters.employee_status || 'ACTIVE'}
                label="Employment Status"
                onChange={(e) => update({ employee_status: e.target.value as 'ACTIVE' | 'INACTIVE' | 'BOTH' })}
                sx={{ '& .MuiSelect-select': { fontSize: FILTER_STANDARD.fontSize } }}
              >
                <MenuItem value="ACTIVE">Active Only</MenuItem>
                <MenuItem value="INACTIVE">Inactive Only</MenuItem>
                <MenuItem value="BOTH">Both Active & Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}
      <Box sx={colBase}>
        <Box sx={{ width: '100%', minWidth: 0 }}>
          <SelectAllMultiSelect
            label="Project Code"
            options={projectOptions}
            selectedValues={filters.project_codes || []}
            onChange={(v) => update({ project_codes: v })}
            allLabel="All projects"
            menuWidth={220}
            maxHeight={350}
          />
        </Box>
      </Box>
      {showManagerFilter && availableManagers.length > 0 && (
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <SelectAllMultiSelect
              label="Direct Manager"
              options={managerOptions}
              selectedValues={filters.managers || []}
              onChange={(v) => update({ managers: v })}
              allLabel="All managers"
            />
          </Box>
        </Box>
      )}
      {showEmployeeFilter && (
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <SelectAllMultiSelect
              label="Team Members"
              options={employeeOptions}
              selectedValues={filters.employee_names || []}
              onChange={(v) => update({ employee_names: v })}
              allLabel="All team members"
            />
          </Box>
        </Box>
      )}
      {showSbuFilter && (
        <Box sx={colBase}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <SelectAllMultiSelect
              label="Sub-Business Unit"
              options={sbuOptions}
              selectedValues={filters.sub_bus || []}
              onChange={(v) => update({ sub_bus: v })}
              allLabel="All SBUs"
            />
          </Box>
        </Box>
      )}
    </FilterStripLayout>
  );
};

export default MyTeamTopFilters;
